import { FC, useMemo, useState } from 'react';
import { Payment, DescriptionCode } from '@lendica/utils';
import { FilterBar } from '../components/FilterBar';
import { NoSchedules } from '../components/NoSchedules';
import { useUpcomingPayments } from '../hooks/useUpcomingPayments';
import { parse, compareDesc, compareAsc } from 'date-fns';
import _ from 'lodash';
import { VirtualizedPaymentsList } from '../components/VirtualizedPaymentsList';

interface UpcomingPaymentsProps {
    payments: Payment[];
    isInitialLoading: boolean;
    isFetchingMore: boolean;
    hasNext: boolean;
    loadMore: () => void;
}

const filterer = (items: Payment[], filters: any) => {
    let filteredItems = items.filter(x =>
        [DescriptionCode.CompanyCollection, DescriptionCode.CompanyDisburse].includes(
            x.description_code
        )
    );
    let filterCategories = Object.keys(filters);

    // sort ascending by date
    filteredItems = filteredItems.sort((a: Payment, b: Payment) => {
        let date1 = parse(a.event_date, 'yyyy-MM-dd', new Date());
        let date2 = parse(b.event_date, 'yyyy-MM-dd', new Date());
        return compareAsc(date1, date2);
    });

    filterCategories.map(category => {
        if (_.isEmpty(filters)) {
            return filteredItems;
        }
        if (category === 'product') {
            if (filters[category].length === 0) {
                return filteredItems;
            } else {
                filteredItems = filteredItems.filter(item =>
                    filters[category].includes(item.product_name)
                );
            }
        }
        if (category === 'dealId') {
            if (filters[category].length === 0) {
                return filteredItems;
            } else {
                filteredItems = filteredItems.filter(x => filters[category].includes(x.deal));
            }
        }
        if (category === 'type') {
            if (filters[category].length === 0) {
                return filteredItems;
            } else {
                filteredItems = filteredItems.filter(x =>
                    filters[category].includes(x.description_code)
                );
            }
        }
    });
    return filteredItems;
};

export const UpcomingPayments: FC<UpcomingPaymentsProps> = ({
    payments,
    isInitialLoading,
    isFetchingMore,
    hasNext,
    loadMore,
}) => {
    const [filters, setFilters] = useState({});
    const currentDate = useMemo(() => new Date(), []);

    // upcoming = today or future
    let filteredByDate = useMemo(
        () =>
            payments.filter((payment: Payment) => {
                const d = parse(payment.event_date, 'yyyy-MM-dd', new Date());
                return compareDesc(d, currentDate) <= 0;
            }),
        [currentDate, payments]
    );

    const { filtersList } = useUpcomingPayments({
        payLater: !_.isEmpty(filteredByDate.filter(payment => payment.product_name == 'paylater')),
        drawDown: !_.isEmpty(filteredByDate.filter(payment => payment.product_name == 'drawdown')),
        fundNow: !_.isEmpty(filteredByDate.filter(payment => payment.product_name == 'fundnow')),
        payments: filteredByDate,
    });

    const onChange = (value: any) => {
        setFilters(value);
    };

    let filtered_payments = useMemo(
        () => filterer(filteredByDate, filters),
        [filteredByDate, filters]
    );

    return (
        <>
            {isInitialLoading ? null : filteredByDate.length !== 0 ? (
                filtered_payments.length === 0 ? (
                    <>
                        <FilterBar
                            filters={filtersList}
                            initialValue={filters}
                            onChange={onChange}
                        />
                        <NoSchedules subtitle="No results matching filters." />
                    </>
                ) : (
                    <>
                        <FilterBar
                            filters={filtersList}
                            initialValue={filters}
                            onChange={onChange}
                        />
                        <VirtualizedPaymentsList
                            items={filtered_payments}
                            variant="upcoming"
                            loadMore={loadMore}
                            hasNext={hasNext}
                            isFetchingMore={isFetchingMore}
                        />
                    </>
                )
            ) : (
                <NoSchedules subtitle="You have no upcoming payments." />
            )}
        </>
    );
};
